import React, { useState } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import {
  CategoryType,
  ContentMetricType,
  GetVoiceAndToneCurrentAndTargetVectorsResponse,
  VoiceAndToneMatchType
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { useContentService } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useContentService';
import {
  ContentDoubleDropdownBarChart,
  ContentDropdownBarChart
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentBarChart';
import { ContentDropdownSplineChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentSplineChart';
import { VoiceAndToneScatterPlot } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/VoiceAndTone/VoiceAndToneScatterPlot';
import { createVoiceAndToneMatchAnalysisColumnDefinitions } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/utils';
import {
  TableGridAdapter,
  VoiceAndToneScatterPlotAdapter
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/TableGridAdapter';
import { useCompanyName, useMainEntityType, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { ProductLevelVoiceAndToneAnalysisChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ProductLevelVoiceAndToneAnalysisChart';
import { ProductLevelVoiceAndToneContentDetails } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ProductLevelVoiceAndToneContentDetails';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { useContentChartSubtitles } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useDynamicSubtitle';
import { formatMetric } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { ProductContent } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/ProductLevelContentAdapter';
import { BarChartData } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import { VoiceAndTonePagePopup } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV2/VoiceAndTonePagePopup';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { TooltipIconComponent } from 'src/components/BeaconV2Components/ExperimentalLayout/ProductGrowthPage';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { ContentTableGrid } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentTableGrid';
import { useStacklineTheme } from '@stackline/ui';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import colors from 'src/utils/colors';

const useVoiceToneMatchTypesFilter = () => {
  const filterParamsFromUrl = new URLSearchParams(window.location.search);
  const filters = JSON.parse(filterParamsFromUrl.get('filter'))?.matchTypes ?? [];
  return filters as VoiceAndToneMatchType[];
};

/**
 * @deprecated
 */
const ContentVoiceAndTonePage = () => {
  const {
    getContentScoresByWeek,
    getContentScoresByProduct,
    getContentScoreByCategory,
    getProductLevelVoiceAndToneAnalysis,
    getProductContent,
    getProductCountByVoiceOrTone
  } = useContentService();

  const { generateBarChartSubtitle, generateSplineChartSubtitle } = useContentChartSubtitles();

  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const theme = useStacklineTheme();
  const entityType = useMainEntityType();
  const companyName = useCompanyName();
  const mainTimePeriodName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodName = useComparisonTimePeriodShortDisplayName();

  const voiceToneMatchTypes = useVoiceToneMatchTypesFilter();

  const [showVoiceAndToneDefinitions, setShowVoiceAndToneDefinitions] = useState(false);

  const groupByFieldOptionsByEntityType = {
    default: [
      {
        id: 'category',
        label: 'Category'
      },
      {
        id: 'subcategory',
        label: 'Subcategory'
      },
      {
        id: 'brand',
        label: 'Brand'
      }
    ],
    brand: [
      {
        id: 'category',
        label: 'Category'
      },
      {
        id: 'subcategory',
        label: 'Subcategory'
      }
    ],
    category: [
      {
        id: 'subcategory',
        label: 'Subcategory'
      },
      {
        id: 'brand',
        label: 'Brand'
      }
    ],
    subcategory: [
      {
        id: 'brand',
        label: 'Brand'
      }
    ]
  };

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: '83.5px' }}>
      {entityType === 'product' && (
        <>
          <ProductLevelVoiceAndToneAnalysisChart
            title="Key Metrics"
            queryFn={() => getProductLevelVoiceAndToneAnalysis()}
            renderSubtitle={(data: GetVoiceAndToneCurrentAndTargetVectorsResponse) => {
              const { toneMatchScore, voiceMatchScore } = data;
              const formattedVoiceMatchScore = formatMetric(voiceMatchScore, METRICTYPE.PERCENT, {
                decimalPlaces: 1
              });
              const formattedToneMatchScore = formatMetric(toneMatchScore, METRICTYPE.PERCENT, {
                decimalPlaces: 1
              });
              return `The voice and tone charts below show the current and target voice and tone qualities of the product content. This product has a voice match score of ${formattedVoiceMatchScore} and a tone match score of ${formattedToneMatchScore}.`;
            }}
          />
          <ProductLevelVoiceAndToneContentDetails
            title="Product Content"
            queryFn={() => getProductContent()}
            renderSubtitle={(data: ProductContent) => {
              const { currentDominantVoice, currentDominantTone } = data;
              return `The section below displays the current content for this product. The content has an ${currentDominantVoice} dominant voice and an ${currentDominantTone} dominant tone.`;
            }}
          />
        </>
      )}

      {entityType !== 'product' && (
        <>
          <VoiceAndToneScatterPlot
            title="Voice & Tone Match Score"
            renderTitle={() => {
              return (
                <Flex gap="sm" alignItems="center">
                  <Text variant="h4">Voice & Tone Match Score</Text>
                  <TooltipIconComponent
                    style={{ cursor: 'pointer', width: '16px' }}
                    onClick={() => setShowVoiceAndToneDefinitions(true)}
                  />
                </Flex>
              );
            }}
            queryFn={async () =>
              new VoiceAndToneScatterPlotAdapter(
                (await getContentScoresByProduct(
                  ContentMetricType.VoiceAndToneMatchAnalysis,
                  undefined,
                  undefined,
                  voiceToneMatchTypes
                )) as TableGridAdapter
              )
            }
            renderSubtitle={() => {
              return `The chart displays the voice and tone match score for ${companyName} with each dot representing a product.`;
            }}
          />

          <ContentDropdownSplineChart
            options={[
              {
                id: 'voice',
                label: 'Voice Match Score',
                queryFn: () =>
                  getContentScoresByWeek(
                    ContentMetricType.VoiceMatchScore,
                    mainTimePeriod,
                    comparisonTimePeriod,
                    voiceToneMatchTypes
                  )
              },
              {
                id: 'tone',
                label: 'Tone Match Score',
                queryFn: () =>
                  getContentScoresByWeek(ContentMetricType.ToneMatchScore, mainTimePeriod, comparisonTimePeriod)
              }
            ]}
            renderSubtitle={(selectedOption: string, score: string, change: number) => {
              return generateSplineChartSubtitle({
                formattedAverage: score,
                metricName: `${selectedOption.toLowerCase()} match score`,
                formattedPercentChange: formatMetric(Math.abs(change), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
                direction: change > 0 ? 'higher' : 'lower'
              });
            }}
          />

          <ContentDropdownBarChart
            renderSubtitle={(selectedOption) => {
              return `The chart displays the count of products for ${companyName} grouped by dominant ${selectedOption} during the ${mainTimePeriodName} time period compared to the ${comparisonTimePeriodName}.`;
            }}
            prefix="Product Count by"
            options={[
              {
                id: 'voice',
                label: 'Voice',
                queryFn: () =>
                  getProductCountByVoiceOrTone('voice', mainTimePeriod, comparisonTimePeriod, voiceToneMatchTypes)
              },
              {
                id: 'tone',
                label: 'Tone',
                queryFn: () =>
                  getProductCountByVoiceOrTone('tone', mainTimePeriod, comparisonTimePeriod, voiceToneMatchTypes)
              }
            ]}
          />

          <ContentDoubleDropdownBarChart
            metrics={[
              {
                id: 'voice',
                label: 'Voice Match Score'
              },
              {
                id: 'tone',
                label: 'Tone Match Score'
              }
            ]}
            groupByFields={groupByFieldOptionsByEntityType[entityType] || groupByFieldOptionsByEntityType.default}
            queryFnMap={{
              voice: {
                category: () =>
                  getContentScoreByCategory(
                    ContentMetricType.VoiceMatchScore,
                    CategoryType.Category,
                    undefined,
                    undefined,
                    voiceToneMatchTypes
                  ),
                subcategory: () =>
                  getContentScoreByCategory(
                    ContentMetricType.VoiceMatchScore,
                    CategoryType.Subcategory,
                    undefined,
                    undefined,
                    voiceToneMatchTypes
                  ),
                brand: () =>
                  getContentScoreByCategory(
                    ContentMetricType.VoiceMatchScore,
                    CategoryType.Brand,
                    undefined,
                    undefined,
                    voiceToneMatchTypes
                  )
              },
              tone: {
                category: () =>
                  getContentScoreByCategory(
                    ContentMetricType.ToneMatchScore,
                    CategoryType.Category,
                    undefined,
                    undefined,
                    voiceToneMatchTypes
                  ),
                subcategory: () =>
                  getContentScoreByCategory(
                    ContentMetricType.ToneMatchScore,
                    CategoryType.Subcategory,
                    undefined,
                    undefined,
                    voiceToneMatchTypes
                  ),
                brand: () =>
                  getContentScoreByCategory(
                    ContentMetricType.ToneMatchScore,
                    CategoryType.Brand,
                    undefined,
                    undefined,
                    voiceToneMatchTypes
                  )
              }
            }}
            renderSubtitle={(selectedMetric: string, selectedGroupBy: string, data: BarChartData) => {
              const count = data.chartData[0].categories.length;

              const groupByText = (() => {
                switch (selectedGroupBy) {
                  case 'category':
                    return count === 1 ? 'from 1 category' : `from all ${count} categories`;
                  case 'subcategory':
                    return count === 1 ? 'from 1 subcategory' : `from all ${count} subcategories`;
                  case 'brand':
                    return count === 1 ? 'from 1 brand' : `from all ${count} brands`;
                  default:
                    return '';
                }
              })();

              return generateBarChartSubtitle({
                metricName: `${selectedMetric.toLowerCase()} match score`,
                groupByText
              });
            }}
          />

          <ContentTableGrid
            title="Product Details"
            mode="table"
            columnDefinitions={[
              {
                id: 'product',
                header: 'Products',
                renderCell: (row) => (
                  <div style={{ width: '321px' }}>
                    <ProductCell stacklineSku={row.stacklineSku} title={row.product.title} />
                  </div>
                )
              },
              ...createVoiceAndToneMatchAnalysisColumnDefinitions(theme)
            ]}
            renderSubtitle={() => {
              return `The table displays the current dominant voice and tone, the voice and tone match scores, as well as the match type for ${companyName} by product.`;
            }}
            queryFn={() =>
              getContentScoresByProduct(
                ContentMetricType.VoiceAndToneMatchAnalysis,
                mainTimePeriod,
                comparisonTimePeriod,
                voiceToneMatchTypes
              )
            }
            tableSx={{
              minWidth: 650,
              borderCollapse: 'unset',
              '& tbody .MuiTableCell-root': {
                padding: '12px 40px',
                height: '60px',
                paddingRight: '0px'
              },
              '& thead .MuiTableCell-root': {
                padding: '0px 0px 16px 40px'
              },
              '& tbody td': {
                borderBottom: '1px solid #f0f0f0'
              },
              '& thead th': {
                border: 'none'
              },
              '& thead th:first-of-type': {
                padding: '0px 0px 16px 16px'
              },
              '& tbody td:first-of-type': {
                borderLeft: '1px solid #f0f0f0',
                padding: '16px 0px 16px 16px'
              },
              '& tbody td:last-child': {
                borderRight: '1px solid #f0f0f0',
                paddingRight: '16px'
              },
              '& tbody tr:first-of-type td': {
                borderTop: '1px solid #f0f0f0'
              },
              '& tbody tr:first-of-type td:first-of-type': {
                borderTopLeftRadius: '8px'
              },
              '& tbody tr:first-of-type td:last-of-type': {
                borderTopRightRadius: '8px'
              },
              '& tbody tr:first-of-type td:last-child': {
                borderTopRightRadius: '8px'
              },
              '& tbody tr:last-child td:first-of-type': {
                borderBottomLeftRadius: '8px'
              },
              '& tbody tr:last-child td:last-child': {
                borderBottomRightRadius: '8px'
              },
              '& thead .MuiTableCell-root span': {
                fontFamily: 'Roboto',
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '20px',
                letterSpacing: '1.2px',
                textAlign: 'left',
                textUnderlinePosition: 'from-font',
                textDecorationSkipInk: 'none',
                color: colors.darkBlue,
                textTransform: 'uppercase'
              }
            }}
          />
        </>
      )}
      {showVoiceAndToneDefinitions && (
        <VoiceAndTonePagePopup
          open={showVoiceAndToneDefinitions}
          onClose={() => setShowVoiceAndToneDefinitions(false)}
        />
      )}
    </BasePageContainer>
  );
};

export default ContentVoiceAndTonePage;
